import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useLoyaltyCustomerStatus } from '../../../APIs/Loyalty/loyalty';
import './LoyaltyOptIn.css';

/** What to say when this person already has a standing with the programme. */
const STANDING = {
  active: {
    icon: 'ph ph-seal-check',
    title: 'Already in the rewards programme',
    detail: 'Nothing to do — they carry on collecting on this booking.'
  },
  pending: {
    icon: 'ph ph-hourglass-medium',
    title: 'Already added, waiting to be confirmed',
    detail: 'The counter will ask them on their next visit, and they join properly then.'
  },
  declined: {
    icon: 'ph ph-hand-palm',
    title: 'They chose not to join',
    detail: 'Asked at the counter and said no, so nobody asks them again.'
  }
};

/**
 * Offering the rewards programme while a booking is being taken.
 *
 * Ticked by default, because the whole point is that staff do not have to
 * remember — which is also why ticking it does not make anybody a member. It
 * records an intention; the counter asks the person out loud on their first
 * visit, and only then does a welcome message go out. A box ticked on their
 * behalf, on a call they did not ask to be on, is not a yes.
 *
 * Drawn only when a programme is actually running, and replaced by a plain
 * statement when this number already has a standing with it — offering to add
 * somebody who joined last March is a control that cannot do anything.
 */
export default function LoyaltyOptIn({ phone = '', checked = true, onChange }) {
  const { offer, status } = useLoyaltyCustomerStatus(phone);

  const standing = STANDING[status];

  if (!offer) return null;

  // A statement, not a control — and the tick the parent is still holding is
  // left alone rather than forced off. The server treats a tick for somebody
  // who has already joined, or already refused, as the no-op it is, so nothing
  // here has to fight the form's state to keep that true.
  if (standing) {
    return (
      <div className={`lo-card lo-card--said lo-card--${status}`}>
        <i className={`${standing.icon} lo-card__mark`} />
        <div className="lo-card__body">
          <span className="lo-card__title">{standing.title}</span>
          <span className="lo-card__detail">{standing.detail}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`lo-card${checked ? ' is-on' : ''}`}>
      <Form.Check
        type="checkbox"
        id="booking-join-loyalty"
        className="lo-card__check"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        label={
          <span className="lo-card__body">
            <span className="lo-card__title">Add them to the rewards programme</span>
            {/* The offer in the restaurant's own numbers, because "join our
                programme" is not something staff can read out to a customer. */}
            <span className="lo-card__offer">
              <i className="ph ph-gift" />
              {offer}
            </span>
            <span className="lo-card__detail">Confirmed at the counter on their first visit, and welcomed by SMS then.</span>
          </span>
        }
      />
    </div>
  );
}

LoyaltyOptIn.propTypes = {
  /** The number as typed. Anything short of ten digits is treated as unknown. */
  phone: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired
};
